import { boxOf, colOf, rowOf } from '../game/geometry.ts';

export interface MiniBoardProps {
  values: number[];
  /** Small digits per empty cell (bitmask). */
  notes?: number[];
  /** Small digits shown faint and struck through (bitmask), e.g. player strike-offs. */
  struck?: number[];
  /** The cells that make the pattern (outlined). */
  pattern?: number[];
  /** A row, column or box to shade softly. */
  area?: number[];
  /** Digits that go in, shown in the accent colour. */
  placements?: { cell: number; digit: number }[];
  /** Candidates removed, shown in red and struck through. */
  eliminations?: { cell: number; digit: number }[];
  /** Repeated digits, shown in red. */
  clash?: number[];
  selected?: number;
  /** Show only part of the board (inclusive, 0-based). */
  rows?: [number, number];
  cols?: [number, number];
  /** What the picture shows, for screen readers. */
  label: string;
}

const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

/** A static picture of (part of) a Sudoku board, for How to play. */
export function MiniBoard(p: MiniBoardProps) {
  const [r0, r1] = p.rows ?? [0, 8];
  const [c0, c1] = p.cols ?? [0, 8];
  const placed = new Map((p.placements ?? []).map((x) => [x.cell, x.digit]));
  const elim = new Map<number, number>();
  for (const e of p.eliminations ?? []) elim.set(e.cell, (elim.get(e.cell) ?? 0) | (1 << e.digit));
  const pattern = new Set(p.pattern);
  const area = new Set(p.area);
  const clash = new Set(p.clash);

  const cells: number[] = [];
  for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) cells.push(r * 9 + c);

  return (
    <div
      className="mini-board"
      role="img"
      aria-label={p.label}
      style={{ gridTemplateColumns: `repeat(${c1 - c0 + 1}, minmax(0, 1fr))`, ['--cols' as string]: c1 - c0 + 1 }}
    >
      {cells.map((cell) => {
        const r = rowOf(cell);
        const c = colOf(cell);
        const v = p.values[cell];
        const put = placed.get(cell);
        const notes = (p.notes?.[cell] ?? 0) | (elim.get(cell) ?? 0);
        const struck = p.struck?.[cell] ?? 0;
        const sel = p.selected;
        const related =
          sel !== undefined && sel !== cell && (rowOf(sel) === r || colOf(sel) === c || boxOf(sel) === boxOf(cell));
        const cls = [
          'mini-cell',
          v ? 'is-given' : '',
          area.has(cell) || related ? 'is-area' : '',
          pattern.has(cell) ? 'is-pattern' : '',
          put ? 'is-target' : '',
          clash.has(cell) ? 'is-clash' : '',
          sel === cell ? 'is-selected' : '',
          c % 3 === 2 && c !== c1 ? 'edge-r' : '',
          r % 3 === 2 && r !== r1 ? 'edge-b' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <div key={cell} className={cls}>
            {v ? (
              <span className="mini-digit">{v}</span>
            ) : put ? (
              <span className="mini-digit is-put">{put}</span>
            ) : notes ? (
              <span className="mini-notes">
                {DIGITS.map((d) => (
                  <span
                    key={d}
                    className={`${elim.get(cell)! & (1 << d) ? 'is-elim' : ''}${struck & (1 << d) ? ' is-struck' : ''}`}
                  >
                    {notes & (1 << d) ? d : ''}
                  </span>
                ))}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
